import "@/lib/sentry"
import type { ResolvedComponent } from "@inertiajs/react"
import { createInertiaApp, router } from "@inertiajs/react"
import * as Sentry from "@sentry/react"
import { type ReactNode, StrictMode } from "react"
import { createRoot, hydrateRoot } from "react-dom/client"

import PersistentLayout from "@/layouts/persistent-layout"
import { inertiaDefaults } from "@/lib/inertia-defaults"

router.on("before", (event) => {
  event.detail.visit.headers["X-Browser-Timezone"] =
    Intl.DateTimeFormat().resolvedOptions().timeZone
})

const appName =
  (import.meta.env.VITE_APP_NAME as string | undefined) ?? "ToDoOrDie"

const sentryEnabled = Boolean(
  document
    .querySelector<HTMLMetaElement>('meta[name="sentry-dsn"]')
    ?.content?.trim(),
)

const rootOptions = sentryEnabled
  ? {
      onUncaughtError: Sentry.reactErrorHandler(),
      onCaughtError: Sentry.reactErrorHandler(),
      onRecoverableError: Sentry.reactErrorHandler(),
    }
  : undefined

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) => (title ? `${title} - ${appName}` : appName),

  resolve: (name) => {
    const pages = import.meta.glob<{ default: ResolvedComponent }>(
      "../pages/**/*.tsx",
      {
        eager: true,
      },
    )
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    page.default.layout ??= (pageElement: ReactNode) => (
      <PersistentLayout>{pageElement}</PersistentLayout>
    )

    return page
  },

  setup({ el, App, props }) {
    if (el.hasChildNodes()) {
      hydrateRoot(
        el,
        <StrictMode>
          <App {...props} />
        </StrictMode>,
        rootOptions,
      )
      return
    }
    createRoot(el, rootOptions).render(
      <StrictMode>
        <App {...props} />
      </StrictMode>,
    )
  },

  defaults: inertiaDefaults,

  progress: {
    color: "#4B5563",
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
    )
  }
})
